// garmentImportOptions — the three import switches, SAVED ON THE GARMENT
// (ENG-1108 follow-up).
//
// `loadGarment` has always taken three booleans, and until now they existed in
// exactly two places, neither of them the asset: the `?garmenthide=` /
// `?garmentcull=` / `?garmentfit=` URL params, and the `loadGarment` node's
// checkboxes. So "Apply to character" from the card menu could only ever use
// the hardcoded defaults, and a garment that needs different ones had no way
// to say so — you had to remember, every time, and type a URL.
//
// They belong to the GARMENT because that is what they are properties of. A
// Marvelous export that includes the character's own shoes wants `hideOutfit`
// off; one draped over a body the rig culls wants `cullSkin` off; one authored
// for another figure is the only case for `applyFit`. None of that changes per
// session, and all of it is the same answer for everyone who applies it.
//
// PRECEDENCE, and it is deliberate: the row is the DEFAULT, the node and the
// URL OVERRIDE. A `loadGarment` node's checkboxes and a deep link's params are
// explicit per-use decisions someone typed for this graph or this link, and a
// saved row must not silently overrule them — the same direction the bundle's
// `face_drives` keys take against `?gaze=` / `?blink=`.
//
// Stored under ONE namespaced key inside the row's existing `metadata` jsonb.
// That keeps this to a client change with no asset-manager deploy and no
// schema migration, and `metadata` is already the row's open field (the MCP's
// `upload_garment` takes it verbatim, so a garment can be uploaded with its
// switches already set). The namespace matters: `metadata` also carries the
// descriptive facts the inspector shows (pieces, vertices, unit), and those
// must survive a write from here.
//
// No three.js and no network, so `node --test` drives it.

/**
 * What a garment loads as when nothing says otherwise.
 *
 * The ONE statement of these values. `loadGarmentNode.js` imports them so a
 * node left untouched expresses the same load as a plain apply, which is the
 * property `garmentNodeOptions` was already asserting by restating the
 * literals — three copies that agreed only by inspection.
 */
export const GARMENT_OPTION_DEFAULTS = Object.freeze({
  // The rig's own outfit is hidden, because a garment REPLACES it — drawn
  // together, the character's shirt intersects the new one.
  hideOutfit: true,
  // Body triangles under the cloth are dropped, or the skin pokes through
  // wherever the two surfaces deform differently.
  cullSkin: true,
  // The advisory fit transform is NOT applied: a garment authored for another
  // figure must LOOK wrong, or nobody learns the asset is for someone else.
  applyFit: false,
})

/** The metadata key everything below reads and writes. */
export const GARMENT_OPTIONS_KEY = 'importOptions'

/** The three switches, in the order the UI shows them. */
export const GARMENT_OPTION_FIELDS = Object.freeze([
  { key: 'hideOutfit', label: "hide the rig's own outfit" },
  { key: 'cullSkin', label: 'cull body skin under the garment' },
  {
    key: 'applyFit',
    label: 'apply the advisory fit transform',
    note: 'A garment authored for another figure should LOOK wrong unless you ask for the fit.',
  },
])

const KEYS = GARMENT_OPTION_FIELDS.map((f) => f.key)

/** A row's raw metadata, whichever casing the API handed back. */
function metadataOf(row) {
  const m = row?.metadata ?? row?.raw?.metadata ?? null
  return m && typeof m === 'object' && !Array.isArray(m) ? m : {}
}

/**
 * The import options saved on a garment row.
 *
 * A key the row OMITS takes the default, and so does a key whose value is not
 * a boolean. Absent is not `false`: reading a missing or malformed value as
 * "off" would turn a row nobody has edited into one that ships the character's
 * outfit and the garment together, and a typo in a hand-written metadata blob
 * would silently change how the garment loads.
 *
 * @param {object|null} row a garment asset row
 * @returns {{hideOutfit: boolean, cullSkin: boolean, applyFit: boolean}}
 */
export function readGarmentOptions(row) {
  const saved = metadataOf(row)[GARMENT_OPTIONS_KEY]
  const src = saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {}
  const out = {}
  for (const key of KEYS) {
    out[key] = typeof src[key] === 'boolean' ? src[key] : GARMENT_OPTION_DEFAULTS[key]
  }
  return out
}

/** Does this row state anything at all, or is it running on the defaults? */
export function hasSavedGarmentOptions(row) {
  const saved = metadataOf(row)[GARMENT_OPTIONS_KEY]
  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return false
  return KEYS.some((k) => typeof saved[k] === 'boolean')
}

/**
 * The `metadata` object to PATCH so `patch` becomes the row's saved options.
 *
 * Every other metadata key is carried over — the inspector's descriptive
 * fields live in the same jsonb, and a PATCH replaces the column whole, so
 * writing a bare `{importOptions}` would delete them.
 *
 * Only the three known switches are written, and only as booleans, so a
 * caller cannot park arbitrary data in here by accident.
 *
 * @param {object|null} row   the row being edited
 * @param {object} patch      any subset of the three switches
 * @returns {object} the whole new metadata value
 */
export function writeGarmentOptions(row, patch) {
  const meta = metadataOf(row)
  const next = { ...readGarmentOptions(row) }
  for (const key of KEYS) {
    if (typeof patch?.[key] === 'boolean') next[key] = patch[key]
  }
  return { ...meta, [GARMENT_OPTIONS_KEY]: next }
}

/**
 * One line naming what a garment will do when applied — for the inspector and
 * the apply status, so the two cannot word it differently.
 *
 * Says what IS happening rather than listing the switches, because the reader
 * is asking "what will this do to my character", not "what is the schema".
 */
export function describeGarmentOptions(opts) {
  const o = { ...GARMENT_OPTION_DEFAULTS, ...(opts ?? {}) }
  const parts = [
    o.hideOutfit ? "hides the rig's outfit" : "keeps the rig's outfit",
    o.cullSkin ? 'culls the skin underneath' : 'keeps the skin underneath',
  ]
  // Mentioned only when ON: it is off for almost every garment, and a line
  // that always ends "fit not applied" trains people to stop reading it.
  if (o.applyFit) parts.push('applies the advisory fit')
  return parts.join(' · ')
}
